// DEV-09 韌體管理：整個範圍的設備本體——目前版本、有沒有新版、自動更新與維護時段（= 排程），以及更新報表。
// 這裡是純邏輯（狀態分類、維護時段檢查、報表彙整、CSV）；資料來自 OTA 既有的 App API。
import type { FirmwareJob, HomeFirmwareStatus } from '../../lib/planeApi';

export type FwState = 'updating' | 'available' | 'latest' | 'unknown';

/** 進行中的 job（跟 OTA 服務的 activeStatuses 同一組）。 */
const ACTIVE = new Set(['pending', 'queued', 'sent', 'accepted', 'downloading', 'installing', 'rebooting']);

export const jobActive = (j?: FirmwareJob | null): boolean => !!j && ACTIVE.has(j.status);

export function fwState(s: HomeFirmwareStatus): FwState {
  if (s.error) return 'unknown';
  if (jobActive(s.job)) return 'updating';
  if (s.update_available) return 'available';
  // 離線的設備報的是上次的版本，不能說「已是最新」（B-24、第二輪 B2-16：離線的 2CPS 仍標「已是最新」）
  if (s.online === false) return 'unknown';
  // 讀不到目前版本（還沒回報）也不能說「已是最新」
  return s.current_version ? 'latest' : 'unknown';
}

export function fleetSummary(list: HomeFirmwareStatus[]): Record<FwState, number> {
  const out: Record<FwState, number> = { updating: 0, available: 0, latest: 0, unknown: 0 };
  for (const s of list) out[fwState(s)]++;
  return out;
}

/** 維護時段「HH:MM-HH:MM」（設備當地時間；可跨午夜；起訖不能相同）。 */
export function validWindow(w: string): boolean {
  const m = /^([01]\d|2[0-3]):([0-5]\d)-([01]\d|2[0-3]):([0-5]\d)$/.exec(w.trim());
  return !!m && `${m[1]}:${m[2]}` !== `${m[3]}:${m[4]}`;
}

export type ReportResult = 'success' | 'failed' | 'other';

export interface ReportRow {
  home: string;
  device: string;
  aid: string;
  from: string;
  to: string;
  status: string;
  result: ReportResult;
  reason: string;
  trigger: string;
  at: string;
  finishedAt: string;
}

export const resultOf = (status: string): ReportResult =>
  status === 'success' ? 'success' : status === 'failed' ? 'failed' : 'other';

/** 每台的更新歷史 → 報表列（只留 sinceMs 之後建立的；新的在前）。 */
export function reportRows(items: { home: string; device: string; aid: string; history: FirmwareJob[] }[], sinceMs: number): ReportRow[] {
  const out: ReportRow[] = [];
  for (const it of items) {
    for (const j of it.history) {
      if (Date.parse(j.created_at) < sinceMs) continue;
      out.push({
        home: it.home,
        device: it.device,
        aid: it.aid,
        from: j.from_version ?? '',
        to: j.version,
        status: j.status,
        result: resultOf(j.status),
        reason: j.reason ?? '',
        trigger: j.trigger,
        at: j.created_at,
        finishedAt: j.finished_at ?? '',
      });
    }
  }
  return out.sort((a, b) => b.at.localeCompare(a.at));
}

export function reportSummary(rows: ReportRow[]): Record<ReportResult, number> {
  const out: Record<ReportResult, number> = { success: 0, failed: 0, other: 0 };
  for (const r of rows) out[r.result]++;
  return out;
}

const cell = (v: string) => {
  // 試算表公式注入：= + - @ 開頭的前面加 '
  const s = /^[=+\-@]/.test(v) ? "'" + v : v;
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

/** 報表 CSV（UTF-8 BOM，Excel 直接開中文不亂碼）。 */
export function reportCsv(rows: ReportRow[], header: string[]): string {
  const lines = [header.map(cell).join(',')];
  for (const r of rows) lines.push([r.at, r.home, r.device, r.aid, r.from, r.to, r.status, r.reason, r.trigger, r.finishedAt].map(cell).join(','));
  return '﻿' + lines.join('\r\n') + '\r\n';
}
